import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { kanbanAPI, contractRequestAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ds';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, Building2, ArrowRight, ExternalLink } from 'lucide-react';
import {
  PIPELINE_STAGE_IDS,
  PIPELINE_STAGE_LABELS,
  statusToStage,
  statusToBadgeVariant,
  stageToCanonicalStatus,
} from '@/lib/contracting/statusMapping';

// Stages considered "terminal" for the Kanban's Show/Hide Completed toggle.
// Kept small and explicit — matches the Requests Page "Other" bucket rules.
const TERMINAL_STAGE_IDS = ['approved', 'other'];

// Column accent colours — one per business stage. Kept inline (not in App.css)
// because these are Kanban-only chrome and don't warrant DS tokens.
const STAGE_COLOR = {
  new:      '#4f46e5',
  review:   '#0284c7',
  missing:  '#e0a83d',
  approved: '#15794a',
  other:    '#64748b',
};

export default function KanbanBoardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isKRRole(user);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moveDialog, setMoveDialog] = useState(null);
  const [moveStatus, setMoveStatus] = useState('');
  const [moveComment, setMoveComment] = useState('');
  const [moving, setMoving] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await kanbanAPI.getBoard();
      setBoard(res.data);
    } catch {
      toast.error('Failed to load Kanban board');
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (isAdmin) loadBoard(); }, [isAdmin, loadBoard]);

  // Bucket the raw-status columns from the backend into business stages via
  // the shared taxonomy. Each business column carries the concatenated card
  // list for every raw status the backend returned under this stage.
  const businessColumns = useMemo(() => {
    if (!board) return [];
    return PIPELINE_STAGE_IDS.map((stageId) => {
      const cards = [];
      for (const col of (board.columns || [])) {
        if (statusToStage(col.key) === stageId) {
          cards.push(...(board.cards?.[col.key] || []));
        }
      }
      return {
        id: stageId,
        label: PIPELINE_STAGE_LABELS[stageId],
        color: STAGE_COLOR[stageId],
        cards,
      };
    });
  }, [board]);

  const visibleColumns = businessColumns.filter(
    (c) => showTerminal || !TERMINAL_STAGE_IDS.includes(c.id)
  );

  const handleMove = async () => {
    if (!moveDialog || !moveStatus) return;
    setMoving(true);
    try {
      await contractRequestAPI.updateStatus(moveDialog.request_id, {
        status: moveStatus,
        comment: moveComment || `Moved to ${PIPELINE_STAGE_LABELS[statusToStage(moveStatus)]} via Kanban board`,
      });
      toast.success(`Moved to ${PIPELINE_STAGE_LABELS[statusToStage(moveStatus)]}`);
      setMoveDialog(null); setMoveStatus(''); setMoveComment('');
      loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move card');
    }
    setMoving(false);
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-slate-500">Only KR admins can access the Kanban board.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="kanban-board-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Operations</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Contract Pipeline</h1>
          <p className="text-sm text-slate-500 mt-0.5">Click any card to move it between business stages.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={showTerminal ? 'default' : 'outline'} size="sm" onClick={() => setShowTerminal(!showTerminal)} className="gap-1.5 text-xs" data-testid="toggle-completed-btn">
            {showTerminal ? 'Hide' : 'Show'} Completed
          </Button>
          <Button variant="outline" size="sm" onClick={loadBoard} className="gap-1.5" data-testid="refresh-board-btn">
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>
      ) : !board ? (
        <div className="text-center py-20 text-slate-500">Failed to load board data</div>
      ) : (
        <>
          {/* Column summary — one badge per business stage */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {visibleColumns.map((col) => (
              <Badge
                key={col.id}
                variant="outline"
                className="whitespace-nowrap px-3 py-1.5 text-xs font-medium"
                style={{ borderColor: col.color, color: col.color }}
                data-testid={`kanban-summary-${col.id}`}
              >
                {col.label}: {col.cards.length}
              </Badge>
            ))}
          </div>

          {/* Kanban board */}
          <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '60vh' }}>
            {visibleColumns.map((col) => (
              <div key={col.id} className="flex-shrink-0 w-64" data-testid={`kanban-column-${col.id}`}>
                {/* Column header */}
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                  <h3 className="text-sm font-semibold text-slate-700">{col.label}</h3>
                  <Badge variant="secondary" className="text-[10px] h-5 px-1.5 ml-auto">{col.cards.length}</Badge>
                </div>

                {/* Cards */}
                <div className="space-y-2 min-h-[200px]">
                  {col.cards.length === 0 ? (
                    <div
                      className="text-center py-8 text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg"
                      data-testid={`kanban-empty-${col.id}`}
                    >
                      No requests in {col.label} stage
                    </div>
                  ) : (
                    col.cards.map((card) => {
                      const rawStatus = card.status || card.current_status;
                      return (
                        <div
                          key={card.request_id}
                          className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md hover:border-slate-300 transition-all cursor-pointer group"
                          onClick={() => { setMoveDialog({ ...card, status: rawStatus }); setMoveStatus(''); setMoveComment(''); }}
                          data-testid={`kanban-card-${card.request_id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span
                              className="text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline truncate max-w-[170px]"
                              onClick={(e) => { e.stopPropagation(); navigate(`/agents/${card.npn}`); }}
                            >
                              {card.agent_name}
                            </span>
                            <ExternalLink
                              size={12}
                              className="text-slate-300 group-hover:text-sky-500 transition-colors flex-shrink-0"
                              onClick={(e) => { e.stopPropagation(); navigate(`/requests/${card.request_id}`); }}
                            />
                          </div>
                          <div className="flex items-center gap-1 text-[11px] text-slate-500 mb-2">
                            <Building2 size={11} />
                            <span className="truncate">{card.carrier_name}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <StatusBadge variant={statusToBadgeVariant(rawStatus)}>
                              {PIPELINE_STAGE_LABELS[statusToStage(rawStatus)]}
                            </StatusBadge>
                            <span className="text-[10px] text-slate-400">{card.created_at?.split('T')[0]}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Move Card Dialog — pick a target business stage; the canonical raw
          status for that stage is sent to the backend. */}
      <Dialog open={!!moveDialog} onOpenChange={(open) => { if (!open) setMoveDialog(null); }}>
        <DialogContent className="max-w-sm" data-testid="kanban-move-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">Move Request</DialogTitle>
            <DialogDescription>
              {moveDialog?.agent_name} — {moveDialog?.carrier_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
              <StatusBadge variant={statusToBadgeVariant(moveDialog?.status)}>
                {PIPELINE_STAGE_LABELS[statusToStage(moveDialog?.status)]}
              </StatusBadge>
              <ArrowRight size={14} className="text-slate-400" />
              {moveStatus
                ? <StatusBadge variant={statusToBadgeVariant(moveStatus)}>
                    {PIPELINE_STAGE_LABELS[statusToStage(moveStatus)]}
                  </StatusBadge>
                : <span className="text-slate-400 text-xs">Select target stage</span>}
            </div>
            <div>
              <Label className="text-xs">Target Stage</Label>
              <Select value={moveStatus} onValueChange={setMoveStatus}>
                <SelectTrigger className="h-9" data-testid="kanban-move-status-select">
                  <SelectValue placeholder="Select target stage…" />
                </SelectTrigger>
                <SelectContent>
                  {PIPELINE_STAGE_IDS.filter((sid) => sid !== statusToStage(moveDialog?.status)).map((sid) => {
                    // Canonical raw status per stage — single source of truth
                    // in /lib/contracting/statusMapping.
                    const canonical = stageToCanonicalStatus(sid);
                    return (
                      <SelectItem key={sid} value={canonical} data-testid={`move-target-${sid}`}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: STAGE_COLOR[sid] }} />
                          {PIPELINE_STAGE_LABELS[sid]}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Comment (optional)</Label>
              <Textarea value={moveComment} onChange={(e) => setMoveComment(e.target.value)} placeholder="Add a note about this stage change…" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog(null)}>Cancel</Button>
            <Button onClick={handleMove} disabled={!moveStatus || moving} data-testid="kanban-move-btn">
              {moving ? <Loader2 size={14} className="animate-spin mr-1" /> : <ArrowRight size={14} className="mr-1" />}
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
